import { apiClient } from "./apiClient";
import { getTeamData } from "./utils/teamDataHelpers";

/**
 * Obtiene la información de alineaciones del partido según el ID
 * @param {string} matchId - ID del partido
 * @param {string} country - País para filtrar datos (parámetro query)
 * @returns {Promise<Object>} Datos de alineaciones del partido
 */
export const getLineups = async (matchId, country = null) => {
  try {
    const response = await apiClient.get(
      `/api/core-clarosports/v2/events/match/${matchId}/lineups`,
      {
        params: {
          ...(country === 'us' && { country })
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching lineups data:", error.message);
    throw error;
  }
};

/**
 * Transforma los datos del API de alineaciones a un formato compatible con los componentes
 * @param {Object} apiData - Datos crudos del API de alineaciones
 * @returns {Object} Alineaciones transformadas
 */
export const transformLineupsData = (apiData) => {
  if (!apiData) return null;

  return {
    home: {
      teamId: apiData.home?.team_id,
      formation: apiData.home?.formation,
      manager: apiData.home?.manager,
      starters: transformPlayers(apiData.home?.starters || []),
      substitutes: transformPlayers(apiData.home?.substitutes || []),
    },
    away: {
      teamId: apiData.away?.team_id,
      formation: apiData.away?.formation,
      manager: apiData.away?.manager,
      starters: transformPlayers(apiData.away?.starters || []),
      substitutes: transformPlayers(apiData.away?.substitutes || []),
    },
  };
};

/**
 * Transforma la información de los jugadores
 * @param {Array} players - Array de jugadores del API
 * @returns {Array} Jugadores transformados
 */
const transformPlayers = (players) => {
  return players.map(player => ({
    playerId: player.player_id,
    name: player.name,
    firstName: player.first_name,
    lastName: player.last_name,
    shirt: player.shirt,
    position: player.position,
    positionLabel: player.position_label,
    captain: player.captain,
    line: player.line,
    order: player.order,
    onPitch: player.on_pitch,
    stats: transformPlayerStats(player.stats || []),
  }));
};

/**
 * Transforma las estadísticas del jugador
 * @param {Array} stats - Array de estadísticas del jugador
 * @returns {Array} Estadísticas transformadas
 */
const transformPlayerStats = (stats) => {
  return stats.map(stat => ({
    key: stat.key,
    label: stat.label,
    value: stat.value,
  }));
};

/**
 * Obtiene la alineación inicial del equipo
 * @param {Object} lineupsData - Datos de alineaciones transformados
 * @param {string} team - 'home' o 'away'
 * @returns {Object} Información de alineación del equipo
 */
export const getTeamLineup = (lineupsData, team) => {
  return getTeamData(lineupsData, team);
};

/**
 * Obtiene los jugadores titulares del equipo
 * @param {Object} lineupsData - Datos de alineaciones transformados
 * @param {string} team - 'home' o 'away'
 * @returns {Array} Lista de jugadores titulares
 */
export const getStarters = (lineupsData, team) => {
  return getTeamData(lineupsData, team, 'starters') || [];
};

/**
 * Obtiene los jugadores suplentes del equipo
 * @param {Object} lineupsData - Datos de alineaciones transformados
 * @param {string} team - 'home' o 'away'
 * @returns {Array} Lista de jugadores suplentes
 */
export const getSubstitutes = (lineupsData, team) => {
  return getTeamData(lineupsData, team, 'substitutes') || [];
};

/**
 * Obtiene la formación táctica del equipo
 * @param {Object} lineupsData - Datos de alineaciones transformados
 * @param {string} team - 'home' o 'away'
 * @returns {string} Formación táctica (ej: "4-3-3")
 */
export const getFormation = (lineupsData, team) => {
  return getTeamData(lineupsData, team, 'formation');
};

/**
 * Obtiene el entrenador del equipo
 * @param {Object} lineupsData - Datos de alineaciones transformados
 * @param {string} team - 'home' o 'away'
 * @returns {string} Nombre del entrenador
 */
export const getManager = (lineupsData, team) => {
  return getTeamData(lineupsData, team, 'manager');
};
